/**
 * ValRecargaDialog — Checkout inline para adquirir pacotes de créditos Val
 * (Phase 11.12). Acionado quando a régua Val atinge ≥ 90% do consumo.
 *
 * Pacotes espelham `ADDONS` de AdminS360Planos.jsx:
 *   • P — 30 créditos · R$ 19  (para bater a virada do mês)
 *   • M — 80 créditos · R$ 39  (recomendado — melhor custo/crédito)
 *   • G — 200 créditos · R$ 79 (equipes maiores)
 *
 * Pré-seleção automática:
 *   - restante ≤ 10  → G  (consumo iminente)
 *   - restante ≤ 30  → M
 *   - senão          → P
 *
 * Modo protótipo: registra a compra em LocalStorage `val_recargas_v1`
 * para histórico e futura integração com gateway (Stripe/PIX).
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, CreditCard, QrCode, FileText, CheckCircle2, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const RECARGAS_LS = "val_recargas_v1";

const PACOTES = [
  { codigo: "P", nome: "Pacote P", creditos: 30,  preco: 19, tag: null,            descricao: "Estica até o fim do mês" },
  { codigo: "M", nome: "Pacote M", creditos: 80,  preco: 39, tag: "Recomendado",   descricao: "Melhor custo por crédito" },
  { codigo: "G", nome: "Pacote G", creditos: 200, preco: 79, tag: "Time inteiro",  descricao: "Para consultorias e equipes maiores" },
];

const FORMAS = [
  { value: "credito", label: "Cartão de Crédito", icon: CreditCard },
  { value: "pix",     label: "PIX",               icon: QrCode },
  { value: "boleto",  label: "Boleto",            icon: FileText },
];

const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

function preSelectByRemaining(restante) {
  if (restante <= 10) return "G";
  if (restante <= 30) return "M";
  return "P";
}

function saveRecarga(rec) {
  try {
    const raw = localStorage.getItem(RECARGAS_LS);
    const list = raw ? JSON.parse(raw) : [];
    const arr = Array.isArray(list) ? list : [];
    arr.unshift({ ...rec, id: `rec-${Date.now()}`, created_at: new Date().toISOString() });
    localStorage.setItem(RECARGAS_LS, JSON.stringify(arr.slice(0, 50)));
  } catch { /* noop */ }
}

export default function ValRecargaDialog({ open, onClose, workspaceId, restante, onSuccess }) {
  const preSel = useMemo(() => preSelectByRemaining(restante ?? 0), [restante]);
  const [selPacote, setSelPacote] = useState(preSel);
  const [forma, setForma] = useState("pix");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Se o dialog reabrir com restante diferente, recalcula pré-seleção
  useEffect(() => {
    if (open) {
      setSelPacote(preSel);
      setDone(false);
      setSubmitting(false);
    }
  }, [open, preSel]);

  const pacote = PACOTES.find(p => p.codigo === selPacote);

  const confirmar = async () => {
    if (!pacote) return;
    setSubmitting(true);
    // Simulação de checkout — 800ms
    await new Promise(r => setTimeout(r, 800));
    saveRecarga({
      workspace_id: workspaceId || "prototype",
      pacote_codigo: pacote.codigo,
      creditos: pacote.creditos,
      valor: pacote.preco,
      forma_pagamento: forma,
    });
    setSubmitting(false);
    setDone(true);
    toast.success("Recarga confirmada", {
      description: `+${pacote.creditos} créditos Val · ${brl(pacote.preco)}`,
    });
    onSuccess?.({ pacote: pacote.codigo, creditos: pacote.creditos });
    // Fecha após 1.2s para dar tempo do usuário ver o Check verde
    setTimeout(() => onClose(), 1200);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dialog-val-recarga">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-600" /> Recarregar créditos Val
          </DialogTitle>
          <DialogDescription>
            Seu consumo do mês está acelerado — <strong>{restante ?? 0}</strong> consulta{(restante ?? 0) === 1 ? "" : "s"} restantes na cota do plano.
            Escolha um pacote adicional para continuar sem interrupção.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-6 text-center" data-testid="recarga-sucesso">
            <div className="w-11 h-11 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-700" />
            </div>
            <div className="text-sm font-bold text-emerald-900">Recarga confirmada</div>
            <p className="text-xs text-emerald-700 mt-1">
              +{pacote.creditos} créditos adicionados ao pool do Val.
            </p>
          </div>
        ) : (
          <>
            {/* Grade de pacotes */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2" data-testid="recarga-pacotes">
              {PACOTES.map(p => {
                const sel = p.codigo === selPacote;
                const custoPorCred = p.preco / p.creditos;
                return (
                  <button
                    key={p.codigo}
                    type="button"
                    onClick={() => setSelPacote(p.codigo)}
                    data-testid={`recarga-pacote-${p.codigo}`}
                    className={cn(
                      "text-left rounded-md border p-3 transition-all",
                      sel
                        ? "border-admin-500 bg-admin-50 ring-2 ring-admin-200"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Pacote</span>
                      {p.tag && (
                        <Badge className={cn(
                          "text-[8px] border-transparent",
                          p.tag === "Recomendado" ? "bg-emerald-600 text-white" : "bg-slate-800 text-white"
                        )}>{p.tag}</Badge>
                      )}
                    </div>
                    <div className="mt-1 flex items-baseline gap-1">
                      <Sparkles className={cn("w-3.5 h-3.5", sel ? "text-admin-700" : "text-slate-400")} />
                      <span className="text-lg font-bold text-slate-900">{p.creditos}</span>
                      <span className="text-[10px] text-slate-500">créditos</span>
                    </div>
                    <div className="mt-2 text-xl font-bold tabular-nums text-admin-800">{brl(p.preco)}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 tabular-nums">
                      {brl(custoPorCred)} / crédito
                    </div>
                    <div className="text-[11px] text-slate-600 mt-2 leading-snug">{p.descricao}</div>
                  </button>
                );
              })}
            </div>

            {/* Forma de pagamento */}
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Forma de pagamento</div>
              <div className="grid grid-cols-3 gap-1.5" data-testid="recarga-formas">
                {FORMAS.map(f => {
                  const Icon = f.icon;
                  const sel = forma === f.value;
                  return (
                    <button
                      key={f.value}
                      type="button"
                      onClick={() => setForma(f.value)}
                      data-testid={`recarga-forma-${f.value}`}
                      className={cn(
                        "h-11 rounded-md border text-[11px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors",
                        sel
                          ? "border-admin-500 bg-admin-50 text-admin-800"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      )}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Resumo final */}
            <div className="mt-3 rounded-md border border-admin-200 bg-admin-50/40 p-3 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-admin-700 font-bold">Total</div>
                <div className="text-2xl font-bold text-admin-900 tabular-nums leading-tight" data-testid="recarga-total">
                  {brl(pacote?.preco || 0)}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  +{pacote?.creditos || 0} créditos · {FORMAS.find(x => x.value === forma)?.label}
                </div>
              </div>
              <div className="text-right text-[10px] text-slate-500 max-w-[180px] leading-snug italic">
                Cobrança única — não altera o valor da sua assinatura recorrente.
              </div>
            </div>
          </>
        )}

        {!done && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={submitting}>Cancelar</Button>
            <Button
              onClick={confirmar}
              disabled={submitting || !pacote}
              className="bg-admin-700 hover:bg-admin-800 text-white gap-2"
              data-testid="btn-confirmar-recarga"
            >
              {submitting ? "Processando…" : <>Confirmar recarga <Zap className="w-3.5 h-3.5" /></>}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
